import SectionCard from '../../components/SectionCard.jsx';
import ConsoleTable from '../../components/ConsoleTable.jsx';
import StatusWord from '../../components/StatusWord.jsx';
import { formatCount, formatMoney, priceSourceWords } from '../../lib/license-format.js';

export const PRODUCTS_ID = 'license-products';

/**
 * "Products" (spec 5.1): purchased, assigned, in use, wasted, unassigned, the price per user per month and the waste
 * per month, sorted by waste per month. While the admin edits prices, the price cell is a field (field border, 3:1);
 * otherwise it shows the amount and whose price it is ("Your price" or "Estimate", a Microsoft list price).
 */
export default function ProductsTable({ products, currency, editing, draft, errors, onDraftChange, onReset, asOf }) {
  const priceCell = (p) => {
    if (p.free) return <span className="text-ipc-muted">Free</span>;
    if (p.appliesTo === 'Company') return <span className="text-ipc-muted">Tenant wide</span>;
    if (editing) {
      const err = errors?.[p.skuId];
      const fieldId = `price-${p.skuId}`;
      return (
        <span className="flex flex-col items-end gap-1">
          <label htmlFor={fieldId} className="sr-only">Price per user per month for {p.name}</label>
          <input
            id={fieldId} data-price-input={p.skuId} inputMode="decimal" autoComplete="off"
            className={`input w-24 py-1 text-right tabular-nums ${err ? 'border-ipc-bad' : ''}`}
            value={draft?.[p.skuId] ?? ''} placeholder="Add price"
            aria-invalid={err ? 'true' : undefined} aria-describedby={err ? `${fieldId}-err` : undefined}
            onChange={(e) => onDraftChange(p.skuId, e.target.value)}
          />
          {err ? <span id={`${fieldId}-err`} className="max-w-[12rem] text-right text-[12px] text-ipc-bad">{err}</span> : null}
          {p.price.source === 'entered' && onReset ? (
            <button type="button" className="tap text-[12px] font-semibold text-ipc-light hover:underline" onClick={() => onReset(p.skuId)}>
              Use the estimate
            </button>
          ) : null}
        </span>
      );
    }
    const amount = formatMoney(p.price.amount, currency, { cents: true });
    if (!amount) return <span className="text-ipc-muted" data-price-missing="">No price</span>;
    const words = priceSourceWords(p.price);
    return (
      <span className="flex flex-col items-end">
        <span className="tabular-nums text-ipc-ink">{amount}</span>
        {words ? (
          <span className="text-[12px] text-ipc-muted" title={p.price.source === 'estimate' ? `Estimate, Microsoft list price on ${asOf}. Your agreement price may differ.` : undefined}>{words}</span>
        ) : null}
      </span>
    );
  };
  const num = (key) => ({ align: 'right', sortValue: (p) => p[key], cell: (p) => <span className="tabular-nums">{formatCount(p[key])}</span> });
  const columns = [
    {
      id: 'product', header: 'Product', sortValue: (p) => p.name.toLowerCase(),
      cell: (p) => (
        <span className="block min-w-[10rem]">
          <span className="font-medium text-ipc-ink">{p.name}</span>
          {p.skuPartNumber && p.skuPartNumber !== p.name ? <span className="block text-[12px] text-ipc-muted">{p.skuPartNumber}</span> : null}
        </span>
      ),
    },
    { id: 'purchased', header: 'Purchased', hideBelow: 'md', ...num('purchased') },
    { id: 'assigned', header: 'Assigned', hideBelow: 'md', ...num('assigned') },
    { id: 'inUse', header: 'In use', hideBelow: 'lg', ...num('inUse') },
    { id: 'wasted', header: 'Wasted', ...num('wasted') },
    { id: 'unassigned', header: 'Unassigned', ...num('unassigned') },
    { id: 'price', header: 'Price per user per month', align: 'right', sortValue: (p) => p.price.amount ?? -1, cell: priceCell },
    { id: 'waste', header: 'Waste per month', align: 'right', sortValue: (p) => p.wastePerMonth ?? -1, cell: (p) => <span className="tabular-nums font-semibold text-ipc-ink">{formatMoney(p.wastePerMonth, currency, { cents: true }) ?? <span className="font-normal text-ipc-muted">--</span>}</span> },
    { id: 'status', header: 'Status', hideBelow: 'md', sortValue: (p) => p.status.word, cell: (p) => <StatusWord tone={p.status.tone}>{p.status.word}</StatusWord> },
  ];
  return (
    <SectionCard id={PRODUCTS_ID} title="Products" description="Sorted by waste per month" testId="licenses-products">
      <ConsoleTable
        label="Products"
        framed={false}
        columns={columns}
        rows={products}
        getRowId={(p) => p.skuId}
        rowName={(p) => p.name}
        searchText={(p) => `${p.name} ${p.skuPartNumber ?? ''} ${p.status.word}`}
        searchPlaceholder="Search products"
        pageSize={editing ? 50 : 10}
      />
    </SectionCard>
  );
}
